import React, { useEffect, useState } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { db } from '../../firebase/config';
import {
    FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt,
    FaSearch, FaFilter, FaDownload, FaSpinner, FaUsers, FaRupeeSign,
    FaCheckCircle, FaClock, FaIdCard, FaTimes
} from 'react-icons/fa';
import './TrainingJoinUsersList.css';

const TrainingJoinUsersList = () => {
    const [trainings, setTrainings] = useState([]);
    const [allParticipants, setAllParticipants] = useState([]);
    const [filteredParticipants, setFilteredParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTraining, setSelectedTraining] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showModal, setShowModal] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [referrerNames, setReferrerNames] = useState({});

    const handleCardClick = (participant) => {
        setSelectedParticipant(participant);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedParticipant(null);
    };

    // Fetch all users to match referrer IDs with user data
    useEffect(() => {
        const fetchAllUsers = async () => {
            try {
                const usersRef = ref(db, 'HTAMS/users');
                const snapshot = await get(usersRef);
                
                if (snapshot.exists()) {
                    const users = snapshot.val();
                    const userMap = {};
                    
                    // Create a map of user data
                    Object.entries(users).forEach(([userId, userData]) => {
                        if (userData) {
                            userMap[userId] = {
                                name: userData.name || userData.displayName || '',
                                phone: userData.phone || userData.mobile || '',
                                email: userData.email || ''
                            };
                        }
                    });
                    
                    setReferrerNames(userMap);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchAllUsers();
    }, []);

    // Fetch trainings and participants
    useEffect(() => {
        const trainingRef = ref(db, 'HTAMS/company/trainings');

        const unsubscribe = onValue(trainingRef, (snapshot) => {
            const data = snapshot.val();

            if (data) {
                const trainingsList = [];
                const participantsList = [];

                Object.entries(data).forEach(([trainingId, trainingData]) => {
                    // Add training info
                    trainingsList.push({
                        id: trainingId,
                        location: trainingData.location || 'N/A',
                        venue: trainingData.venue || 'N/A',
                        startDate: trainingData.startDate,
                        endDate: trainingData.endDate,
                        fees: trainingData.fees || 0,
                        trainerName: trainingData.trainerName || 'N/A'
                    });

                    // Add participants
                    if (trainingData.participants) {
                        Object.entries(trainingData.participants).forEach(([participantId, participant]) => {
                            participantsList.push({
                                id: participantId,
                                trainingId,
                                trainingLocation: trainingData.location || 'N/A',
                                trainingStartDate: trainingData.startDate,
                                trainingFees: trainingData.fees || 0,
                                name: participant.name || 'N/A',
                                email: participant.email || 'N/A',
                                phone: participant.phone || participant.mobile || 'N/A',
                                firmName: participant.firmName || 'N/A',
                                address: participant.address || 'N/A',
                                city: participant.city || 'N/A',
                                state: participant.state || 'N/A',
                                pin: participant.pin || 'N/A',
                                aadhar: participant.aadhar || 'N/A',
                                dateOfBirth: participant.dateOfBirth || 'N/A',
                                registeredAt: participant.registeredAt || participant.createdAt || Date.now(),
                                paymentStatus: participant.paymentStatus || 'pending',
                                paymentId: participant.paymentDetails?.razorpay_payment_id || participant.paymentDetails?.payment_link_id || participant.paymentId || 'N/A',
                                amountPaid: participant.feePaid || participant.paymentDetails?.amount_paid || participant.amountPaid || 0,
                                referredBy: participant.referredBy || null,
                                mode: participant.paymentDetails?.payment_method || participant.mode || 'N/A'
                            });
                        });
                    }
                });

                setTrainings(trainingsList);
                setAllParticipants(participantsList);
                setFilteredParticipants(participantsList);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter and sort participants
    useEffect(() => {
        let filtered = [...allParticipants];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.phone.includes(searchTerm) ||
                p.firmName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Training filter
        if (selectedTraining !== 'all') {
            filtered = filtered.filter(p => p.trainingId === selectedTraining);
        }

        // Status filter
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(p => p.paymentStatus === selectedStatus);
        }

        // Sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'date':
                    comparison = new Date(a.registeredAt) - new Date(b.registeredAt);
                    break;
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'training':
                    comparison = a.trainingLocation.localeCompare(b.trainingLocation);
                    break;
                case 'amount':
                    comparison = a.amountPaid - b.amountPaid;
                    break;
                default:
                    comparison = 0;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        setFilteredParticipants(filtered);
    }, [searchTerm, selectedTraining, selectedStatus, sortBy, sortOrder, allParticipants]);

    // Export to CSV
    const exportToCSV = () => {
        const headers = [
            'Name', 'Email', 'Phone', 'Firm Name', 'Address', 'City', 'State', 'PIN',
            'Aadhar', 'Date of Birth', 'Training Location', 'Training Start Date',
            'Mode', 'Fees', 'Amount Paid', 'Payment Status', 'Payment ID',
            'Registered At', 'Referred By'
        ];

        const rows = filteredParticipants.map(p => [
            p.name,
            p.email,
            p.phone,
            p.firmName,
            p.address,
            p.city,
            p.state,
            p.pin,
            p.aadhar,
            p.dateOfBirth,
            p.trainingLocation,
            p.trainingStartDate ? new Date(p.trainingStartDate).toLocaleDateString() : 'N/A',
            p.mode,
            p.trainingFees,
            p.amountPaid,
            p.paymentStatus,
            p.paymentId,
            new Date(p.registeredAt).toLocaleString(),
            p.referredBy ? `${p.referredBy.name} (${p.referredBy.id})` : 'Direct'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `training-participants-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const renderParticipantModal = () => {
        if (!showModal || !selectedParticipant) return null;

        return (
            <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content participant-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Participant Details</h2>
                        <button className="close-button" onClick={closeModal}>
                            <FaTimes />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Full Name</label>
                                <p>{selectedParticipant.name}</p>
                            </div>
                            <div className="detail-item">
                                <label>Email Address</label>
                                <p>{selectedParticipant.email}</p>
                            </div>
                            <div className="detail-item">
                                <label>Phone Number</label>
                                <p>{selectedParticipant.phone}</p>
                            </div>
                            <div className="detail-item">
                                <label>Firm Name</label>
                                <p>{selectedParticipant.firmName}</p>
                            </div>
                            <div className="detail-item full-width">
                                <label>Address</label>
                                <p>{selectedParticipant.address}, {selectedParticipant.city}, {selectedParticipant.state} - {selectedParticipant.pin}</p>
                            </div>
                            <div className="detail-item">
                                <label>Aadhar Number</label>
                                <p>{selectedParticipant.aadhar}</p>
                            </div>
                            <div className="detail-item">
                                <label>Date of Birth</label>
                                <p>{selectedParticipant.dateOfBirth}</p>
                            </div>
                            <div className="detail-item">
                                <label>Training Location</label>
                                <p>{selectedParticipant.trainingLocation}</p>
                            </div>
                            <div className="detail-item">
                                <label>Training Date</label>
                                <p>{selectedParticipant.trainingStartDate ? new Date(selectedParticipant.trainingStartDate).toLocaleDateString() : 'N/A'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Payment Status</label>
                                <span className={`status-badge ${selectedParticipant.paymentStatus}`}>
                                    {selectedParticipant.paymentStatus}
                                </span>
                            </div>
                            <div className="detail-item">
                                <label>Amount Paid</label>
                                <p className="amount">₹{selectedParticipant.amountPaid.toLocaleString()}</p>
                            </div>
                            <div className="detail-item">
                                <label>Payment Mode</label>
                                <p>{selectedParticipant.mode}</p>
                            </div>
                            <div className="detail-item">
                                <label>Payment ID</label>
                                <p className="payment-id">{selectedParticipant.paymentId}</p>
                            </div>
                            <div className="detail-item">
                                <label>Registered At</label>
                                <p>{new Date(selectedParticipant.registeredAt).toLocaleString()}</p>
                            </div>
                            <div className="detail-item full-width">
                                <label>Referred By</label>
                                <p>{
                                    (() => {
                                        if (!selectedParticipant.referredBy) return 'Direct';
                                        
                                        // Get the referrer ID from different possible formats
                                        let referrerId = '';
                                        
                                        if (typeof selectedParticipant.referredBy === 'string') {
                                            referrerId = selectedParticipant.referredBy;
                                        } else if (selectedParticipant.referredBy.id) {
                                            referrerId = selectedParticipant.referredBy.id;
                                        } else if (typeof selectedParticipant.referredBy === 'object') {
                                            // Try to find an ID in the object
                                            const possibleId = Object.values(selectedParticipant.referredBy)
                                                .find(val => typeof val === 'string' && val.length > 10);
                                            if (possibleId) referrerId = possibleId;
                                        }
                                        
                                        // If we have a referrer ID, look it up
                                        if (referrerId && referrerNames[referrerId]) {
                                            return referrerNames[referrerId].name || referrerId;
                                        }
                                        
                                        // If we have a direct name in the object, use it
                                        if (selectedParticipant.referredBy.name) {
                                            return selectedParticipant.referredBy.name;
                                        }
                                        
                                        // If we still have a referrer ID but no name, return the ID
                                        if (referrerId) {
                                            return referrerId;
                                        }
                                        
                                        return 'Direct';
                                    })()
                                }</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="training-users-container">
                <div className="loading-container">
                    <FaSpinner className="loading-icon" />
                    <h3>Loading Participants...</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="training-users-container">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <FaUsers className="title-icon" />
                        Training Join Users List
                    </h1>
                    <p className="page-subtitle">
                        Total Participants: {filteredParticipants.length} of {allParticipants.length}
                    </p>
                </div>
                <button className="export-btn" onClick={exportToCSV}>
                    <FaDownload /> Export to CSV
                </button>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, or firm..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filter-controls">
                    <div className="filter-group">
                        <label><FaFilter /> Training:</label>
                        <select
                            value={selectedTraining}
                            onChange={(e) => setSelectedTraining(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Trainings</option>
                            {trainings.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.location} - {t.startDate ? new Date(t.startDate).toLocaleDateString() : 'TBD'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label><FaFilter /> Status:</label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Status</option>
                            <option value="success">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label><FaFilter /> Sort By:</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="filter-select"
                        >
                            <option value="date">Registration Date</option>
                            <option value="name">Name</option>
                            <option value="training">Training</option>
                            <option value="amount">Amount</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Order:</label>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="filter-select"
                        >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Participants List */}
            <div className="participants-grid">
                {filteredParticipants.length > 0 ? (
                    filteredParticipants.map((participant) => (
                        <div
                            key={participant.id}
                            className="participant-card compact-card clickable"
                            onClick={() => handleCardClick(participant)}
                        >
                            <div className="card-header compact-header">
                                <div className="participant-main-info">
                                    <div className="participant-name">
                                        <FaUser className="icon" />
                                        <h3>{participant.name}</h3>
                                    </div>
                                    {participant.firmName && participant.firmName !== 'N/A' && (
                                        <span className="participant-firm">{participant.firmName}</span>
                                    )}
                                </div>
                                <div className={`payment-badge compact ${participant.paymentStatus}`}>
                                    {participant.paymentStatus === 'success' ? <FaCheckCircle /> : <FaClock />}
                                    {participant.paymentStatus}
                                </div>
                            </div>

                            <div className="card-body compact-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="label">Contact</span>
                                        <div className="value-group">
                                            <span><FaEnvelope className="tiny-icon" /> {participant.email}</span>
                                            <span><FaPhone className="tiny-icon" /> {participant.phone}</span>
                                        </div>
                                    </div>

                                    <div className="info-item">
                                        <span className="label">Location</span>
                                        <div className="value-group">
                                            <span><FaMapMarkerAlt className="tiny-icon" /> {participant.city}, {participant.state}</span>
                                        </div>
                                    </div>

                                    <div className="info-item full-width training-bg">
                                        <span className="label">Training Details</span>
                                        <div className="value-group horizontal">
                                            <span><strong>{participant.trainingLocation}</strong></span>
                                            <span>
                                                {participant.trainingStartDate
                                                    ? new Date(participant.trainingStartDate).toLocaleDateString('en-IN', {
                                                        day: 'numeric', month: 'short', year: '2-digit'
                                                    })
                                                    : 'TBD'
                                                }
                                            </span>
                                            <span className="mode-badge mini">{participant.mode}</span>
                                        </div>
                                    </div>

                                    <div className="info-item full-width payment-bg">
                                        <div className="value-group horizontal space-between">
                                            <span className="amount">₹{participant.amountPaid.toLocaleString()}</span>
                                            <span className="date">Reg: {new Date(participant.registeredAt).toLocaleDateString('en-IN')}</span>
                                        </div>
                                        {participant.paymentId !== 'N/A' && (
                                            <div className="payment-id-mini">{participant.paymentId}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-data">
                        <FaUsers className="no-data-icon" />
                        <h3>No Participants Found</h3>
                        <p>Try adjusting your filters or search criteria</p>
                    </div>
                )}
            </div>
            {renderParticipantModal()}
        </div>
    );
};

export default TrainingJoinUsersList;
